import { useState } from 'react';
import { AlertTriangle, Download, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@loader/ui';
import { Button } from '@loader/ui';
import { Separator } from '@loader/ui';
import { Badge } from '@loader/ui';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@loader/ui';

import { FoldingCube } from '@/components/common';
import {
  useOperationEventSummary,
  useOperationEvents,
  type OperationContext,
  type OperationEvent,
} from '@/features/versions/hooks';
import { useRpc } from '@/lib/rpc';

interface ValidationErrorsProps {
  /** Operation context with tableId and operationId */
  operationContext: OperationContext;
}

export function ValidationErrors({ operationContext }: ValidationErrorsProps) {
  const rpc = useRpc();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch error summary using operation context
  const { data: summary, isLoading: isSummaryLoading, error: summaryError } = useOperationEventSummary(operationContext);

  // Fetch first 50 errors (only when expanded)
  const { data: eventsData, isLoading: isEventsLoading } = useOperationEvents(
    isExpanded ? operationContext : undefined,
    { page: 1, limit: 50, level: 'validation_error' }
  );

  // Handle CSV export
  const handleExport = async () => {
    setIsExporting(true);
    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].operations[':operationId'].events.export.$get({
        param: { id: operationContext.tableId, operationId: operationContext.operationId },
      });

      if (res.ok) {
        const blob = await res.blob();

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `operation-events-${operationContext.operationId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch {
      // Export failed silently - UI already shows export button state
    } finally {
      setIsExporting(false);
    }
  };

  // Format error code for display
  const formatErrorCode = (code: string): string => {
    return code
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Loading state
  if (isSummaryLoading) {
    return (
      <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <CardTitle className="text-yellow-800 dark:text-yellow-200">Validation Errors</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <FoldingCube text="Loading error summary..." size="sm" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (summaryError) {
    return null; // Silently fail if no errors are found
  }

  // No errors
  if (!summary || summary.totalErrors === 0) {
    return null;
  }

  return (
    <Card className="border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <div>
              <CardTitle className="text-yellow-800 dark:text-yellow-200">
                Validation Errors
              </CardTitle>
              <CardDescription className="text-yellow-700 dark:text-yellow-300">
                {summary.totalErrors} validation {summary.totalErrors === 1 ? 'error' : 'errors'} found in this version
              </CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting}
            className="border-yellow-300 hover:bg-yellow-100 dark:border-yellow-700 dark:hover:bg-yellow-900"
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Summary by Type */}
        {summary.byErrorCode.length > 0 && (
          <div>
            <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-200">
              Errors by Type
            </h4>
            <div className="flex flex-wrap gap-2">
              {summary.byErrorCode.map((errorType: { error_code: string; count: number }) => (
                <Badge
                  key={errorType.error_code}
                  variant="secondary"
                  className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                >
                  {formatErrorCode(errorType.error_code)}: {errorType.count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Error Summary by Field */}
        {summary.byField.length > 0 && (
          <div>
            <h4 className="mb-2 font-semibold text-yellow-800 dark:text-yellow-200">
              Top Fields with Errors
            </h4>
            <div className="flex flex-wrap gap-2">
              {summary.byField.slice(0, 10).map((field: { field_id: string | null; field_name: string | null; count: number }, index: number) => (
                <Badge
                  key={field.field_id || `unknown-${index}`}
                  variant="outline"
                  className="border-yellow-300 text-yellow-800 dark:border-yellow-700 dark:text-yellow-200"
                >
                  {field.field_name || 'Unknown Field'}: {field.count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <Separator className="bg-yellow-200 dark:bg-yellow-800" />

        {/* Expandable Detailed Errors */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full justify-between text-yellow-800 hover:bg-yellow-100 dark:text-yellow-200 dark:hover:bg-yellow-900"
          >
            <span>View Detailed Errors (First 50)</span>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>

          {isExpanded && (
            <div className="mt-4">
              {isEventsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <FoldingCube text="Loading errors..." size="sm" />
                </div>
              ) : eventsData && eventsData.events.length > 0 ? (
                <div className="max-h-96 overflow-auto rounded-md border border-yellow-200 dark:border-yellow-800">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-yellow-100 dark:bg-yellow-900">
                        <TableHead className="text-yellow-800 dark:text-yellow-200">Row</TableHead>
                        <TableHead className="text-yellow-800 dark:text-yellow-200">Field</TableHead>
                        <TableHead className="text-yellow-800 dark:text-yellow-200">Error Type</TableHead>
                        <TableHead className="text-yellow-800 dark:text-yellow-200">Message</TableHead>
                        <TableHead className="text-yellow-800 dark:text-yellow-200">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {eventsData.events.map((event: OperationEvent) => (
                        <TableRow key={event.id} className="hover:bg-yellow-50 dark:hover:bg-yellow-950">
                          <TableCell className="font-mono text-xs">{event.row_number ?? '-'}</TableCell>
                          <TableCell className="font-medium">{event.field_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">
                              {formatErrorCode(event.error_code || 'UNKNOWN')}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{event.message}</TableCell>
                          <TableCell className="max-w-xs truncate font-mono text-xs">
                            {event.details && 'actual_value' in event.details
                              ? String(event.details.actual_value)
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-center text-sm text-yellow-700 dark:text-yellow-300">
                  No detailed errors available
                </p>
              )}

              {eventsData && eventsData.pagination.total > 50 && (
                <p className="mt-2 text-xs text-yellow-700 dark:text-yellow-300">
                  Showing first 50 of {eventsData.pagination.total} errors. Export CSV to view all.
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
